import React, { useState } from 'react';
import {
  FaUserTie, FaSearch, FaFilter, FaStar, FaMapMarkerAlt, FaEnvelope, FaPhone,
  FaGlobe, FaBriefcase, FaAward
} from 'react-icons/fa';
import { PROFESSIONAL_TYPES } from './resourcesConstants';
import { RequestProButton } from '../../components/professionals/RequestProButton';
import { ComingSoonAdvisors } from '../../components/professionals/ComingSoonAdvisors';

export function ProfessionalsTab({ professionals, filterType, setFilterType, searchQuery, setSearchQuery, onBook }) {
  return (
    <div data-testid="professionals-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or specialty..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
              data-testid="professional-search"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <FaFilter className="text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
            data-testid="professional-filter"
          >
            <option value="">All Types</option>
            {Object.entries(PROFESSIONAL_TYPES).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <RequestProButton testId="resources-request-pro-btn" />
      </div>

      {/* Professional Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {professionals.map((prof) => (
          <ProfessionalCard key={prof.professional_id} professional={prof} onBook={onBook} />
        ))}
      </div>

      {professionals.length === 0 && (
        <ComingSoonAdvisors
          typeKey={filterType}
          typeLabel={PROFESSIONAL_TYPES[filterType]?.label}
          testId="resources-coming-soon"
        />
      )}
    </div>
  );
}

function ProfessionalCard({ professional, onBook }) {
  const [expanded, setExpanded] = useState(false);
  const typeInfo = PROFESSIONAL_TYPES[professional.professional_type] || { icon: FaUserTie, label: 'Professional', color: 'gray' };
  const TypeIcon = typeInfo.icon;

  return (
    <div
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition"
      data-testid={`professional-card-${professional.professional_id}`}
    >
      {professional.founding_advisor && (
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold px-4 py-1 text-center flex items-center justify-center gap-1">
          <FaAward /> FOUNDING ADVISOR
        </div>
      )}
      {professional.is_featured && !professional.founding_advisor && (
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold px-4 py-1 text-center">
          FEATURED EXPERT
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start space-x-4">
          <div className={`w-14 h-14 bg-${typeInfo.color}-100 rounded-full flex items-center justify-center flex-shrink-0`}>
            <TypeIcon className={`text-${typeInfo.color}-600 text-xl`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-800 truncate">{professional.name}</h3>
            <p className="text-sm text-gray-600">{typeInfo.label}</p>
            {professional.company && (
              <p className="text-sm text-gray-500">{professional.company}</p>
            )}
          </div>
        </div>

        {professional.bio && (
          <div className="mt-4">
            <p className={`text-gray-600 text-sm ${expanded ? '' : 'line-clamp-3'}`}>{professional.bio}</p>
            {professional.bio.length > 150 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-blue-600 text-sm mt-1 font-medium hover:underline"
                data-testid={`toggle-bio-${professional.professional_id}`}
              >
                {expanded ? 'Show less' : 'View full bio →'}
              </button>
            )}
          </div>
        )}

        {expanded && (
          <div className="mt-3 space-y-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            {(professional.location || professional.city) && (
              <div className="flex items-center gap-2">
                <FaMapMarkerAlt className="text-gray-400" />
                {professional.location || `${professional.city}${professional.state ? `, ${professional.state}` : ''}`}
              </div>
            )}
            {professional.email && (
              <div className="flex items-center gap-2">
                <FaEnvelope className="text-gray-400" />
                <a href={`mailto:${professional.email}`} className="text-blue-600 hover:underline">{professional.email}</a>
              </div>
            )}
            {professional.phone && (
              <div className="flex items-center gap-2">
                <FaPhone className="text-gray-400" />
                <a href={`tel:${professional.phone}`} className="text-blue-600 hover:underline">{professional.phone}</a>
              </div>
            )}
            {professional.website && (
              <div className="flex items-center gap-2">
                <FaGlobe className="text-gray-400" />
                <a href={professional.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{professional.website}</a>
              </div>
            )}
            {professional.years_experience && (
              <div className="flex items-center gap-2">
                <FaBriefcase className="text-gray-400" />
                {professional.years_experience}+ years experience
              </div>
            )}
          </div>
        )}

        {professional.specialties && professional.specialties.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {professional.specialties.slice(0, 3).map((specialty) => (
              <span key={specialty} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                {specialty}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="flex items-center text-amber-500">
            <FaStar className="mr-1" />
            <span className="font-medium">{professional.rating || '5.0'}</span>
            <span className="text-gray-400 text-sm ml-1">({professional.review_count || 0})</span>
          </div>
          {(professional.location || professional.city) && !expanded && (
            <div className="flex items-center text-gray-500 text-sm">
              <FaMapMarkerAlt className="mr-1" />
              {professional.location || `${professional.city}${professional.state ? `, ${professional.state}` : ''}`}
            </div>
          )}
        </div>

        <button
          onClick={() => onBook(professional)}
          className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          data-testid={`book-btn-${professional.professional_id}`}
        >
          Book Consultation
        </button>
      </div>
    </div>
  );
}
